// frontend/src/lib/kmlCanvas.js

const WIDTH = 330;
const HEIGHT = 240;

export function buildLegendState(metadata) {
  const hasCategory = !!metadata.category_data;

  const s1 = Number(metadata.shikii1);
  const s2 = Number(metadata.shikii2);
  const s3 = Number(metadata.shikii3);
  const s4 = Number(metadata.shikii4);
  const s5 = Number(metadata.shikii5);

  const eqNum = (a, b) => Number.isFinite(a) && Number.isFinite(b) && a === b;
  const fmt = (v) => (v == null || v === "" || Number.isNaN(Number(v)) ? "" : String(v));

  const hide4 = !hasCategory && eqNum(s4, s3);
  const hide5 = !hasCategory && (eqNum(s5, s4) || eqNum(s4, s3));

  const rangeLabels = [
    `${fmt(s1)}〜`,
    `${fmt(s2)}〜${fmt(s1)}`,
    `${fmt(s3)}〜${fmt(s2)}`,
    `${fmt(s4)}〜${fmt(s3)}`,
    `〜${fmt(s4)}`,
  ];

  const legendText = (idx) => {
    if (hasCategory) return metadata[`hanrei${idx}_name`] ?? "";
    return rangeLabels[idx - 1] ?? "";
  };

  return {
    hide4,
    hide5,
    legendText,
  };
}

function drawClosedPath(ctx, points) {
  ctx.beginPath();
  ctx.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i][0], points[i][1]);
  }
  ctx.closePath();
}

function fillPolygon(ctx, points, fillStyle) {
  drawClosedPath(ctx, points);
  ctx.fillStyle = fillStyle;
  ctx.fill();
}

function fillAndStrokePolygon(ctx, points, fillStyle) {
  drawClosedPath(ctx, points);
  ctx.fillStyle = fillStyle;
  ctx.fill();
  ctx.stroke();
}

function strokeLine(ctx, x1, y1, x2, y2) {
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
}

function fillLegendBox(ctx, x1, y1, x2, y2, color) {
  fillPolygon(
    ctx,
    [
      [x2, y2],
      [x1, y2],
      [x1, y1],
      [x2, y1],
    ],
    color
  );
}

function drawLegendBase(ctx, alpha) {
  ctx.globalAlpha = alpha * 0.75;
  fillPolygon(
    ctx,
    [
      [160.6, 99.2],
      [0.0, 99.2],
      [0.0, 0.0],
      [160.6, 0.0],
      [160.6, 99.2],
    ],
    "rgb(254, 254, 254)"
  );
}

function drawYearPanel(ctx, alpha, year) {
  ctx.globalAlpha = alpha * 0.75;
  ctx.lineWidth = 0.3;
  fillAndStrokePolygon(
    ctx,
    [
      [156.0, 97.4],
      [123.8, 97.4],
      [123.8, 85.9],
      [156.0, 85.9],
      [156.0, 97.4],
    ],
    "rgb(254, 254, 254)"
  );

  ctx.globalAlpha = alpha * 1.0;
  ctx.font = "9.0px 'KozGoPro-Regular-90ms-RKSJ-H'";
  ctx.fillStyle = "rgb(0, 0, 0)";
  ctx.fillText(year, 128.0, 95.1);
}

export function drawLegendCommon(ctx, metadata, rr_ratio) {
  const alpha = ctx.globalAlpha;
  const { hide4, hide5, legendText } = buildLegendState(metadata);

  ctx.save();

  drawLegendBase(ctx, alpha);
  ctx.globalAlpha = alpha * 1.0;

  fillPolygon(
    ctx,
    [
      [105.0, 48.0],
      [92.3, 49.2],
      [82.7, 92.5],
      [84.8, 94.0],
      [90.3, 94.2],
      [115.5, 52.9],
      [105.0, 48.0],
    ],
    metadata.hanrei1_color
  );

  fillPolygon(
    ctx,
    [
      [89.8, 65.7],
      [85.0, 61.7],
      [74.5, 62.0],
      [74.3, 94.0],
      [77.7, 95.9],
      [82.8, 94.9],
      [89.8, 65.7],
    ],
    metadata.hanrei2_color
  );

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(89.5, 65.9);
  ctx.lineTo(79.3, 66.7);
  ctx.lineTo(74.6, 62.3);
  ctx.lineWidth = 0.8;
  ctx.strokeStyle = "rgb(254, 254, 254)";
  ctx.lineCap = "round";
  ctx.stroke();
  strokeLine(ctx, 77.5, 95.7, 79.3, 66.7);
  ctx.restore();

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(115.3, 53.2);
  ctx.lineTo(101.7, 53.4);
  ctx.lineTo(92.5, 49.5);
  ctx.lineWidth = 0.8;
  ctx.strokeStyle = "rgb(254, 254, 254)";
  ctx.lineCap = "round";
  ctx.stroke();
  strokeLine(ctx, 85.2, 94.2, 101.7, 53.4);
  ctx.restore();

  fillPolygon(
    ctx,
    [
      [75.2, 80.4],
      [71.2, 75.0],
      [62.2, 76.7],
      [64.7, 93.2],
      [68.0, 96.0],
      [73.7, 94.4],
      [75.2, 80.4],
    ],
    metadata.hanrei3_color
  );

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(62.5, 76.9);
  ctx.lineTo(67.3, 81.9);
  ctx.lineTo(74.8, 80.5);
  ctx.lineWidth = 0.8;
  ctx.strokeStyle = "rgb(254, 254, 254)";
  ctx.lineCap = "round";
  ctx.stroke();
  strokeLine(ctx, 68.0, 96.0, 67.3, 81.9);
  ctx.restore();

  ctx.beginPath();
  ctx.moveTo(117.3, 53.2);
  ctx.lineTo(122.4, 53.2);
  ctx.lineWidth = 0.5;
  ctx.lineCap = "round";
  ctx.stroke();

  strokeLine(ctx, 120.1, 53.2, 120.1, 94.4);

  fillPolygon(
    ctx,
    [
      [122.3, 57.5],
      [117.7, 57.4],
      [120.1, 52.9],
      [122.3, 57.5],
    ],
    "rgb(0, 0, 0)"
  );

  fillPolygon(
    ctx,
    [
      [117.8, 89.9],
      [122.4, 90.0],
      [120.1, 94.5],
      [117.8, 89.9],
    ],
    "rgb(0, 0, 0)"
  );

  ctx.font = "5.0px 'KozGoPro-Regular-90ms-RKSJ-H'";
  const ratioLabel =
    rr_ratio != null && rr_ratio !== ""
      ? `  (${rr_ratio}倍)`
      : "";

  ctx.fillText((metadata.height_ratio || "") + ratioLabel, 110, 50);

  ctx.font = "5.0px 'KozGoPro-Regular-90ms-RKSJ-H'";
  ctx.fillText("高さ：", 122.8, 62);
  ctx.fillText(metadata.hanrei_height_name, 122.8, 70.0);
  ctx.fillText(metadata.hanrei_height_name_2 || "", 122.8, 77.0);
  ctx.fillText(metadata.hanrei_height_name_3 || "", 122.8, 84.0);

  ctx.fillText("色：" + metadata.hanrei_color_name, 5.8, 54.3);

  ctx.font = "bold 7.0px 'KozGoPro-Regular-90ms-RKSJ-H'";
  ctx.fillText(
    metadata.title_height_name +
      (metadata.title_color_name ? "と" : "") +
      (metadata.title_color_name || ""),
    5,
    45.0
  );

  strokeLine(ctx, 5.0, 56.7, 90.8, 56.7);

  ctx.beginPath();
  ctx.moveTo(91.9, 56.9);
  ctx.bezierCurveTo(91.9, 57.5, 91.4, 58.0, 90.8, 58.0);
  ctx.bezierCurveTo(90.2, 58.0, 89.7, 57.5, 89.7, 56.9);
  ctx.bezierCurveTo(89.7, 56.3, 90.2, 55.8, 90.8, 55.8);
  ctx.bezierCurveTo(91.4, 55.8, 91.9, 56.3, 91.9, 56.9);
  ctx.closePath();
  ctx.fill();

  fillLegendBox(ctx, 5.7, 61.0, 11.2, 66.4, metadata.hanrei1_color);
  ctx.font = "5.0px 'KozGoPro-Regular-90ms-RKSJ-H'";
  ctx.fillStyle = "rgb(0, 0, 0)";
  ctx.fillText(legendText(1), 13.5, 65.2);

  fillLegendBox(ctx, 5.7, 68.5, 11.2, 73.9, metadata.hanrei2_color);
  ctx.fillStyle = "rgb(0, 0, 0)";
  ctx.fillText(legendText(2), 13.5, 72.7);

  fillLegendBox(ctx, 5.7, 76.0, 11.2, 81.4, metadata.hanrei3_color);
  ctx.fillStyle = "rgb(0, 0, 0)";
  ctx.fillText(legendText(3), 13.5, 80.2);

  if (!hide4 && metadata.hanrei4_color) {
    fillLegendBox(ctx, 5.9, 83.5, 11.4, 88.9, metadata.hanrei4_color);
    ctx.fillStyle = "rgb(0, 0, 0)";
    ctx.fillText(legendText(4), 13.7, 87.7);

    fillPolygon(
      ctx,
      [
        [64.7, 85.6],
        [60.7, 82.9],
        [54.3, 86.0],
        [59.2, 96.3],
        [62.3, 97.5],
        [66.2, 95.2],
        [64.7, 85.6],
      ],
      metadata.hanrei4_color
    );

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(54.7, 86.1);
    ctx.lineTo(59.2, 88.3);
    ctx.lineTo(64.5, 85.9);
    ctx.lineWidth = 0.8;
    ctx.strokeStyle = "rgb(254, 254, 254)";
    ctx.lineCap = "round";
    ctx.stroke();
    strokeLine(ctx, 62.3, 97.5, 59.2, 88.3);
    ctx.restore();
  }

  if (!hide5 && metadata.hanrei5_color) {
    fillLegendBox(ctx, 5.9, 91.5, 11.4, 96.4, metadata.hanrei5_color);
    ctx.fillStyle = "rgb(0, 0, 0)";
    ctx.fillText(legendText(5), 13.7, 95.2);

    fillPolygon(
      ctx,
      [
        [56.7, 90.6],
        [53.7, 87.9],
        [48.3, 91.0],
        [51.2, 96.3],
        [54.3, 97.5],
        [58.2, 95.2],
        [56.7, 90.6],
      ],
      metadata.hanrei5_color
    );
  }

  ctx.restore();
}

export function drawLegendYear(ctx, year) {
  const alpha = ctx.globalAlpha;
  ctx.save();
  drawYearPanel(ctx, alpha, year);
  ctx.restore();
}

function dataUrlToBase64(dataUrl) {
  const idx = dataUrl.indexOf("base64,");
  if (idx < 0) throw new Error("not base64 data url");
  return dataUrl.slice(idx + "base64,".length);
}

export function makeLegendCommonPng(meta, rr_ratio, onlyOneYear = false) {
  const canvas = document.createElement("canvas");
  canvas.width = WIDTH * 2;
  canvas.height = HEIGHT * 2;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d context not available");

  ctx.setTransform(2, 0, 0, 2, 0, 0);

  if (onlyOneYear) ctx.translate(0, -35);

  drawLegendCommon(ctx, meta, rr_ratio);

  return {
    filename: `${meta.no}_common.png`,
    base64: dataUrlToBase64(canvas.toDataURL("image/png")),
  };
}

export function makeLegendYearPngsForYears(meta, years, onlyOneYear = false) {
  const result = [];

  for (const year of years) {
    if (!year) continue;

    const canvas = document.createElement("canvas");
    canvas.width = WIDTH * 2;
    canvas.height = HEIGHT * 2;

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2d context not available");

    ctx.setTransform(2, 0, 0, 2, 0, 0);

    if (onlyOneYear) ctx.translate(0, -35);

    drawLegendYear(ctx, year);

    result.push({
      year,
      filename: `${meta.no}_${year}_year.png`,
      base64: dataUrlToBase64(canvas.toDataURL("image/png")),
    });
  }

  return result;
}

export { WIDTH, HEIGHT };